/**
 * Navigation utility functions for TicketEasy module
 */

/**
 * Builds a URL for navigating to a specific message view
 * @param baseUrl The base URL to use (e.g., '/ticketEasy/messages')
 * @param viewType The type of view to show ('allMessages', 'myMessages', 'pendingMyApproval')
 * @param ticketId Optional ticket ID to highlight
 * @returns A formatted URL with query parameters
 */
export const buildMessageViewUrl = (
    baseUrl: string, 
    viewType: 'allMessages' | 'myMessages' | 'pendingMyApproval',
    ticketId?: string
): string => {
    const params = new URLSearchParams();
    
    // Add view type parameter
    params.set('viewType', viewType);
    
    // Add highlight parameter if ticket ID is provided
    if (ticketId) {
        params.set('highlight', ticketId);
    }
    
    return `${baseUrl}?${params.toString()}`;
};

/**
 * Returns the appropriate view type parameter for User Names
 * @param displayName The friendly User Name
 * @returns The corresponding view type parameter
 */
export const getMessageViewTypeByName = (displayName: string): 'allMessages' | 'myMessages' | 'pendingMyApproval' => {
    switch (displayName.toLowerCase()) {
        case 'all messages':
            return 'allMessages';
        case 'my messages':
            return 'myMessages';
        case 'pending my approval':
            return 'pendingMyApproval';
        default:
            return 'allMessages';
    }
};

/**
 * Maps backend view types to frontend view types
 * @param viewType The backend view type
 * @returns The corresponding frontend view type
 */
export const mapToFrontendViewType = (viewType: string): 'allMessages' | 'myMessages' | 'pendingMyApproval' => {
    switch (viewType) {
        case 'all':
            return 'allMessages';
        case 'created':
            return 'myMessages';
        case 'approver':
            return 'pendingMyApproval';
        case 'allMessages':
        case 'myMessages':
        case 'pendingMyApproval':
            return viewType;
        default:
            return 'allMessages';
    }
};

/**
 * Maps frontend view types to backend view types
 * @param viewType The frontend view type
 * @returns The corresponding backend view type
 */
export const mapToBackendViewType = (viewType: string): 'all' | 'created' | 'approver' => {
    switch (viewType) {
        case 'allMessages':
            return 'all';
        case 'myMessages':
            return 'created';
        case 'pendingMyApproval':
            return 'approver';
        case 'all':
        case 'created':
        case 'approver':
            return viewType as any;
        default:
            return 'all';
    }
};
